import type { ProjectTask } from '../types';

export type SubtaskStructureRef = { id: string; name: string };

/** Read structure assignments from a subtask (supports legacy single-field storage). */
export function getSubtaskStructures(task: Pick<ProjectTask, 'structureId' | 'structureName' | 'structureIds' | 'structureNames'>): SubtaskStructureRef[] {
  if (task.structureIds?.length) {
    return task.structureIds.map((id, i) => ({
      id,
      name: (task.structureNames?.[i] || '').trim() || id,
    }));
  }
  if (task.structureId) {
    return [{ id: task.structureId, name: (task.structureName || '').trim() || task.structureId }];
  }
  return [];
}

/** Primary structure used for panel grouping (first selected). */
export function primarySubtaskStructure(task: Pick<ProjectTask, 'structureId' | 'structureName' | 'structureIds' | 'structureNames'>): SubtaskStructureRef | null {
  const list = getSubtaskStructures(task);
  return list[0] ?? null;
}

/** Collapsed pill label, e.g. "Main Barn" or "Main Barn +2". */
export function formatSubtaskStructurePill(task: Pick<ProjectTask, 'structureId' | 'structureName' | 'structureIds' | 'structureNames'>): string | null {
  const list = getSubtaskStructures(task);
  if (!list.length) return null;
  if (list.length === 1) return list[0].name;
  return `${list[0].name} +${list.length - 1}`;
}

/** Full tooltip / title for all assigned structures. */
export function formatSubtaskStructuresTitle(task: Pick<ProjectTask, 'structureId' | 'structureName' | 'structureIds' | 'structureNames'>): string | undefined {
  const list = getSubtaskStructures(task);
  if (!list.length) return undefined;
  return list.map((s) => s.name).join(', ');
}

export function subtaskStructurePayload(structures: SubtaskStructureRef[]): Pick<ProjectTask, 'structureId' | 'structureName' | 'structureIds' | 'structureNames'> {
  if (!structures.length) {
    return {
      structureId: undefined,
      structureName: undefined,
      structureIds: undefined,
      structureNames: undefined,
    };
  }
  const ids = structures.map((s) => s.id);
  const names = structures.map((s) => s.name);
  return {
    structureId: ids[0],
    structureName: names[0],
    structureIds: ids,
    structureNames: names,
  };
}

export function taskMatchesStructureFilterIds(
  task: Pick<ProjectTask, 'structureId' | 'structureName' | 'structureIds' | 'structureNames'>,
  filter: Set<string | '__unassigned'>,
): boolean {
  const list = getSubtaskStructures(task);
  if (!list.length) return filter.has('__unassigned');
  return list.some((s) => filter.has(s.id));
}
